<template>
  <div class="entity-list-page">
    <header class="list-header">
      <div>
        <router-link to="/clients" class="back-link">← Portfolio</router-link>
        <h1>All clients</h1>
        <p class="list-sub">{{ countLabel }}</p>
      </div>
      <button type="button" class="btn btn-primary" @click="openClientModal()">
        <i class="fas fa-plus"></i> Add client
      </button>
    </header>

    <div class="toolbar">
      <div class="search-wrap">
        <i class="fas fa-search"></i>
        <input
          v-model="query"
          type="search"
          placeholder="Search clients…"
          aria-label="Search clients"
        />
      </div>
      <label class="archive-toggle">
        <input v-model="showArchived" type="checkbox" />
        <span>Show archived</span>
      </label>
    </div>

    <div v-if="loading" class="state">Loading clients…</div>
    <div v-else-if="error" class="state error">{{ error }}</div>
    <div v-else-if="filtered.length === 0" class="state">
      No clients match.
      <button type="button" class="linkish" @click="openClientModal()">Add a client</button>
    </div>
    <ul v-else class="entity-card-grid">
      <li
        v-for="client in filtered"
        :key="client.id"
        class="entity-card"
        :class="{ 'entity-card--archived': client.archived }"
      >
        <h3 class="entity-card-title">
          <button type="button" @click="editClient(client)">{{ client.name }}</button>
        </h3>
        <p class="entity-card-desc">
          {{ client.email || 'No email on file' }}
        </p>
        <dl class="entity-card-meta">
          <div>
            <dt>Industry</dt>
            <dd>{{ client.industry_type_display || '—' }}</dd>
          </div>
          <div v-if="client.archived" style="margin-top: 0.25rem">
            <dt>Status</dt>
            <dd>Archived</dd>
          </div>
        </dl>
        <div class="entity-card-actions">
          <button type="button" class="btn btn-primary" @click="editClient(client)">Edit</button>
          <router-link
            :to="`/clients/${client.id}/workflow-settings`"
            class="btn btn-ghost"
          >
            Workflows
          </router-link>
        </div>
      </li>
    </ul>

    <div v-if="showClientModal" class="modal-overlay" @click.self="closeClientModal">
      <div class="modal-content">
        <header class="modal-header">
          <h3>{{ editingClient ? 'Edit client' : 'Add client' }}</h3>
          <button type="button" class="close-btn" @click="closeClientModal"><i class="fas fa-times"></i></button>
        </header>
        <form class="modal-form" @submit.prevent="saveClient">
          <label>Name *<input v-model="clientForm.name" required maxlength="255" /></label>
          <label>Email *<input v-model="clientForm.email" type="email" required /></label>
          <label>Phone<input v-model="clientForm.phone" /></label>
          <label>
            Industry
            <select v-model="clientForm.industry_type">
              <option value="INSURANCE">Insurance</option>
              <option value="CONSTRUCTION">Construction</option>
              <option value="REAL_ESTATE">Real Estate</option>
              <option value="TECHNOLOGY">Technology</option>
              <option value="HEALTHCARE">Healthcare</option>
              <option value="OTHER">Other</option>
            </select>
          </label>
          <div class="modal-actions">
            <button type="button" class="btn btn-ghost" @click="closeClientModal">Cancel</button>
            <button type="submit" class="btn btn-primary">{{ editingClient ? 'Save' : 'Create' }}</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash-es';
import api from '@/services/api';
import { UI_CONSTANTS } from '@/constants';

function asList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.clients)) return payload.clients;
  return [];
}

export default {
  name: 'PortfolioClientsList',
  data() {
    return {
      clients: [],
      loading: true,
      error: null,
      query: '',
      debouncedQuery: '',
      showArchived: false,
      showClientModal: false,
      editingClient: null,
      clientForm: {
        name: '',
        email: '',
        phone: '',
        industry_type: 'INSURANCE',
        business_type: 'CORPORATION',
        description: '',
        archived: false,
      },
      searchDebounce: null,
    };
  },
  computed: {
    filtered() {
      let list = this.showArchived
        ? this.clients
        : this.clients.filter((c) => !c.archived);
      const q = this.debouncedQuery.trim().toLowerCase();
      if (q) {
        list = list.filter((c) => {
          const hay = [c.name, c.email, c.phone, c.industry_type_display, c.description]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return hay.includes(q);
        });
      }
      return list;
    },
    countLabel() {
      return `${this.filtered.length} shown · ${this.clients.filter((c) => !c.archived).length} active`;
    },
  },
  watch: {
    query() {
      this.searchDebounce();
    },
  },
  created() {
    this.searchDebounce = debounce(() => {
      this.debouncedQuery = this.query;
    }, UI_CONSTANTS.SEARCH_DEBOUNCE_MS || 300);
    this.fetchClients();
  },
  methods: {
    async fetchClients() {
      this.loading = true;
      this.error = null;
      try {
        const response = await api.get('/clients/');
        this.clients = asList(response.data);
      } catch (err) {
        this.error = err.message || 'Failed to load clients';
      } finally {
        this.loading = false;
      }
    },
    openClientModal() {
      this.editingClient = null;
      this.clientForm = {
        name: '',
        email: '',
        phone: '',
        industry_type: 'INSURANCE',
        business_type: 'CORPORATION',
        description: '',
        archived: false,
      };
      this.showClientModal = true;
    },
    editClient(client) {
      this.editingClient = client;
      this.clientForm = {
        name: client.name || '',
        email: client.email || '',
        phone: client.phone || '',
        industry_type: client.industry_type || 'OTHER',
        business_type: client.business_type || 'CORPORATION',
        description: client.description || '',
        archived: !!client.archived,
      };
      this.showClientModal = true;
    },
    closeClientModal() {
      this.showClientModal = false;
      this.editingClient = null;
    },
    async saveClient() {
      try {
        if (this.editingClient) {
          await api.put(`/clients/${this.editingClient.id}/`, this.clientForm);
        } else {
          await api.post('/clients/', this.clientForm);
        }
        await this.fetchClients();
        this.closeClientModal();
      } catch (err) {
        this.error = err.response?.data?.detail || err.message || 'Save failed';
      }
    },
  },
};
</script>

<style scoped>
/* List chrome: global .entity-list-page / .entity-card-grid (spoke-entity-cards) */

.search-wrap {
  flex: 1;
  min-width: 12rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.65rem;
  background: #fff;
}

.search-wrap input {
  border: none;
  outline: none;
  width: 100%;
  font: inherit;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-content {
  background: #fff;
  border-radius: 1rem;
  width: 100%;
  max-width: 28rem;
  padding: 1.25rem;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.modal-header h3 { margin: 0; }

.close-btn {
  border: none;
  background: transparent;
  cursor: pointer;
  color: #64748b;
}

.modal-form {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.modal-form label {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  font-size: 0.85rem;
  font-weight: 600;
}

.modal-form input,
.modal-form select {
  font: inherit;
  font-weight: 400;
  padding: 0.5rem 0.65rem;
  border-radius: 0.5rem;
  border: 1px solid #cbd5e1;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.5rem;
}
</style>
